import React from "react";
// Meter Header

// Default
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// Mui
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";

// Types
import { ToastState } from "../../types";

// Custom
import ConsumerDetailLeftCard from "./ConsumerDetailLeftCard";
import ConsumerDetailRightCard from "./ConsumerDetailRightCard";
import CustomToast from "../../components/toast/CustomToast";
import ConfirmationModal from "../modals/ConfirmationModal";

// Theme
import theme from "../../theme";

// Redux
import {
  setCustomerDetails,
  setCustomerId,
  setHasSearched,
  setIsCustomerLoading,
} from "../../redux/features/CustomerSlice";
import { setAllMeters, setTotalMeters } from "../../redux/features/MeterSlice";

// Custom
import CustomSearch from "../search/Search";

// Utils
import { fetchGETRequest, fetchPUTRequest } from "../../utils/services";
import { formatDate } from "../../utils/helper";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";

const ConsumerHeader: React.FC = () => {
  const currentTimestamp = Date.now();
  const matches = useMediaQuery(theme.breakpoints.down("lg"));
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });
  const { hasSearched, customerId, isCustomerLoading } = useAppSelector(
    (state) => state.customer
  );
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [chip, setChip] = useState<string | null>(null);
  const [timeStamp, setTimeStamp] = useState<{
    start: number;
    end: number;
  }>({
    start: currentTimestamp - 24 * 60 * 60 * 1000,
    end: currentTimestamp,
  });
  const [read, setRead] = useState<{
    date: string;
    value: string;
  }>({
    date: "-",
    value: "-",
  });
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    data: any;
    type: string;
  }>({
    open: false,
    data: null,
    type: "",
  });
  const [statusLoader, setStatusLoader] = useState<boolean>(false);

  useEffect(() => {
    if (location?.state) {
      dispatch(setHasSearched(true));
      handleSearch();
    } else {
      dispatch(setHasSearched(false));
      dispatch(setCustomerId(""));
    }
  }, [location]);

  useEffect(() => {
    window.history.replaceState(null, "");
  }, []);

  const handleSearch = async () => {
    try {
      dispatch(setIsCustomerLoading(true));

      const [responseLeft, responseRight] = await Promise.all([
        fetchGETRequest(`v2/Customers/${customerId || location?.state}`),
        fetchGETRequest(`v2/Meters/by-customer/${customerId || location?.state}`),
      ]);

      if (responseLeft?.status === 400 || responseLeft?.status === 404) {
        setTimeout(() => {
          dispatch(setIsCustomerLoading(false));
          setToast({ message: "Consumer not found", severity: "error" });
        }, 3000);
        return;
      }

      if (responseLeft?.success && responseLeft?.data) {
        dispatch(setCustomerDetails(responseLeft.data));
      } else if (responseLeft && !responseLeft.success && !responseLeft.data) {
        // Fallback or V1 style response handling if applicable
        dispatch(setCustomerDetails(responseLeft));
      }

      if (responseRight?.data?.length > 0) {
        dispatch(setTotalMeters(responseRight.data?.length));

        const sortedData = responseRight.data.slice().reverse();

        dispatch(setAllMeters(sortedData));

        await getMeterConnectionStatus(sortedData?.[0]?.id?.id);
        await getTimeSeriesOfMeter(sortedData?.[0]?.id?.id);
      }

      // setTimeout(() => {
      dispatch(setHasSearched(true));
      dispatch(setIsCustomerLoading(false));
      // }, 3000);
    } catch (error: any) {
      dispatch(setIsCustomerLoading(false));
      setToast({ message: error.message, severity: "error" });
    }
  };

  const getTimeSeriesOfMeter = async (deviceID: string) => {
    try {
      const response = await fetchGETRequest(
        `plugins/telemetry/DEVICE/${deviceID}/values/timeseries?keys=IP_KWH&startTs=${
          timeStamp?.start
        }&endTs=${timeStamp?.end}&limit=${10}`
      );

      if (response) {
        setRead({
          date:
            (response?.["IP_KWH"]?.[0]?.ts &&
              formatDate(
                new Date(response?.["IP_KWH"]?.[0]?.ts),
                "DD MMM. 'YY"
              )) ||
            "-",
          value: response?.["IP_KWH"]?.[0]?.value || "-",
        });
      } else {
        setRead({
          date: "-",
          value: "-",
        });
      }
    } catch (error) {
      console.log(error, "---getTimeSeriesOfMeter error---");
    }
  };

  const getMeterConnectionStatus = async (deviceID: string) => {
    try {
      const response = await fetchGETRequest(
        `devices/${deviceID}/connectionstatus`
      );

      if (response?.data?.Status) {
        switch (response?.data?.Status?.trim().toUpperCase()) {
          case "DISCONNECT SUCCESS":
            return setChip("Disconnect");
          case "CONNECT SUCCESS":
            return setChip("Connect");
          case "DISCONNECT IN PROGRESS":
            return setChip("Disconnect In Progress");
          case "CONNECT IN PROGRESS":
            return setChip("Connect In Progress");
          default:
            return null;
        }
      } else {
        setChip(null);
      }
    } catch (error) {
      setChip(null);
    }
  };

  const toggleStatusChange = async () => {
    try {
      setStatusLoader(true);
      let body = {
        deviceIds: [confirmModal?.data?.id],
        command: "",
        projectName: "",
      };
      if (confirmModal?.type === "connect") {
        body.command = "ExecuteConnect";
      } else {
        body.command = "ExecuteDisconnect";
      }

      const response = await fetchPUTRequest(`devices/command`, body);
      getMeterConnectionStatus(confirmModal?.data?.id);
    } catch (error: any) {
      setToast({
        message: error.message,
        severity: "error",
      });
    } finally {
      setStatusLoader(false);
      setConfirmModal({
        open: false,
        data: null,
        type: "",
      });
    }
  };

  return (
    <Box className="consumer">
      <CustomToast
        open={toast?.message}
        severity={toast?.severity}
        setToast={setToast}
      />
      <Grid container spacing={matches ? 1 : 2.25}>
        {!hasSearched && (
          <Grid
            size={{ xs: 12, md: 12, lg: 12 }}
            container
            alignItems={"center"}>
            <Card
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                padding: "14px",
              }}>
              <Typography variant="body1">Consumer ID </Typography>
              <CustomSearch
                value={customerId}
                onChange={(e) => dispatch(setCustomerId(e.target.value))}
                onClick={handleSearch}
                sx={{ width: "500px" }}
                placeholder={"Search with consumer Id"}
              />
            </Card>
          </Grid>
        )}
        {hasSearched && !isCustomerLoading && (
          <Grid size={{ xs: 12, md: 7.5, lg: 7.5 }}>
            <ConsumerDetailLeftCard handleSearch={handleSearch} />
          </Grid>
        )}
        {hasSearched && !isCustomerLoading && (
          <Grid size={{ xs: 12, md: 4.5, lg: 4.5 }}>
            <ConsumerDetailRightCard
              handleActiveMeterStatus={(id: string) => {
                getMeterConnectionStatus(id);
                getTimeSeriesOfMeter(id);
              }}
              read={read}
              chipValue={chip}
              toggleStatusChange={toggleStatusChange}
              setConfirmModal={setConfirmModal}
            />
          </Grid>
        )}
      </Grid>
      <ConfirmationModal
        confirmModal={confirmModal?.open}
        onClose={() =>
          setConfirmModal({
            open: false,
            data: null,
            type: "",
          })
        }
        onClick={toggleStatusChange}
        body={"Are you sure want to change meter status."}
        isLoading={statusLoader}
      />
    </Box>
  );
};

export default ConsumerHeader;
